import { useEffect, useRef, useState } from "react";
import { ScanLine } from "lucide-react";
import { Html5Qrcode, Html5QrcodeSupportedFormats as F } from "html5-qrcode";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { inputCls } from "@/components/ui-kit";

const FORMATS = [F.EAN_13, F.EAN_8, F.UPC_A, F.UPC_E, F.CODE_128, F.CODE_39, F.ITF, F.QR_CODE];

export default function BarcodeScanner({ open, onClose, onDetected }) {
  const { t } = useApp();
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");
  const cb = useRef({ onDetected, onClose });
  cb.current = { onDetected, onClose };

  useEffect(() => {
    if (!open) return undefined;
    setErr("");
    let scanner;
    let done = false;
    const timer = setTimeout(async () => {
      try {
        scanner = new Html5Qrcode("barcode-region", { formatsToSupport: FORMATS, verbose: false });
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 260, height: 130 } }, (text) => {
          if (done) return;
          done = true;
          cb.current.onDetected(text.trim());
          cb.current.onClose();
        }, () => {});
      } catch (e) { setErr(t("camera_error")); }
    }, 200);
    return () => {
      clearTimeout(timer);
      if (scanner?.isScanning) scanner.stop().then(() => scanner.clear()).catch(() => {});
    };
  }, [open, t]);

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    onDetected(manual.trim());
    setManual("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogTitle className="font-heading text-2xl flex items-center gap-2"><ScanLine className="w-6 h-6" />{t("scan_barcode")}</DialogTitle>
        <div id="barcode-region" className="w-full min-h-[220px] rounded-xl overflow-hidden bg-[#0B2A5B]" />
        {err && <p className="text-destructive font-semibold text-sm" data-testid="barcode-camera-error">{err}</p>}
        <form onSubmit={submitManual} className="flex gap-2">
          <input autoFocus dir="ltr" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={t("manual_code")} className={inputCls} data-testid="barcode-manual-input" />
          <button type="submit" className="h-12 px-5 rounded-full bg-primary text-white font-bold" data-testid="barcode-manual-submit">OK</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
